import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PAYMENTS_ENABLED } from '@redevicer/shared'
import { buildExchangeOfferMessage, type ExchangeRoute } from '@redevicer/shared'
import { listingsAPI, offersAPI, paymentsAPI, sellerVerificationAPI } from '../api/client'

interface MyListing {
  id: number
  title: string
  price: number
  phone_model: { manufacturer: string; model_name: string }
}

interface ExchangeOfferModalProps {
  listingId: number
  currentPrice: number
  isOpen: boolean
  onClose: () => void
  token: string
  overlayClassName?: string
  onSubmitted?: () => void
  onGoToAccount?: () => void
}

export default function ExchangeOfferModal({
  listingId,
  currentPrice,
  isOpen,
  onClose,
  token,
  overlayClassName,
  onSubmitted,
  onGoToAccount,
}: ExchangeOfferModalProps) {
  const { t } = useTranslation()
  const [myListings, setMyListings] = useState<MyListing[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [extraCashText, setExtraCashText] = useState('')
  const [exchangeRoute, setExchangeRoute] = useState<ExchangeRoute>('direct')
  const [passActive, setPassActive] = useState(false)
  const [verified, setVerified] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMine, setLoadingMine] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setExchangeRoute('direct')
    setLoadingMine(true)
    const requests = PAYMENTS_ENABLED
      ? [
          listingsAPI.getMyListings(token),
          paymentsAPI.exchangePassStatus(token),
          sellerVerificationAPI.getStatus(token),
        ]
      : [listingsAPI.getMyListings(token), sellerVerificationAPI.getStatus(token)]
    Promise.all(requests)
      .then((results) => {
        const listRes = results[0]
        const passRes = PAYMENTS_ENABLED ? results[1] : null
        const verifyRes = PAYMENTS_ENABLED ? results[2] : results[1]
        const items = (listRes.data as MyListing[]).filter((l) => l.id !== listingId)
        setMyListings(items)
        setPassActive(PAYMENTS_ENABLED ? !!passRes?.data?.active : false)
        setVerified(!!verifyRes.data?.can_sell)
        if (items.length > 0) {
          setSelectedId(items[0].id)
          const diff = Math.max(0, currentPrice - items[0].price)
          setExtraCashText(diff > 0 ? diff.toFixed(2) : '')
        }
      })
      .catch(() => setMyListings([]))
      .finally(() => setLoadingMine(false))
  }, [isOpen, listingId, currentPrice, token])

  function parseExtraCash(): number {
    const n = parseFloat(extraCashText.replace(',', '.'))
    return Number.isFinite(n) && n >= 0 ? n : 0
  }

  function formatSuggestedCash(diff: number): string {
    return diff > 0 ? diff.toFixed(2) : ''
  }

  function handleExtraCashChange(raw: string) {
    const v = raw.replace(',', '.')
    if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setExtraCashText(v)
  }

  function handleExtraCashFocus(e: React.FocusEvent<HTMLInputElement>) {
    if (extraCashText === '0' || extraCashText === '0.00') {
      setExtraCashText('')
    }
    e.target.select()
  }

  function routeBlocked(): string | null {
    if (exchangeRoute === 'warehouse') {
      if (!passActive) return t('exchange.passRequired')
      if (!verified) return t('platformVerify.required')
    }
    return null
  }

  function submit() {
    if (!selectedId) {
      alert('Pasirinkite savo skelbimą mainams.')
      return
    }
    const block = routeBlocked()
    if (block) {
      const go = window.confirm(`${block}\n\n${t('sellerVerify.goToAccount')}?`)
      if (go) onGoToAccount?.()
      return
    }
    const extraCash = parseExtraCash()
    const offered = (myListings.find((l) => l.id === selectedId)?.price ?? 0) + extraCash
    setLoading(true)
    const message = buildExchangeOfferMessage(selectedId, extraCash, exchangeRoute)
    offersAPI
      .createOffer(
        {
          listing_id: listingId,
          offered_price: offered,
          message,
        },
        token,
      )
      .then(() => {
        alert('Mainų pasiūlymas išsiųstas! Pardavėjas gaus pranešimą.')
        onClose()
        onSubmitted?.()
      })
      .catch((err) => alert(`Klaida: ${err.response?.data?.detail || err.message}`))
      .finally(() => setLoading(false))
  }

  if (!isOpen) return null

  const extraCash = parseExtraCash()
  const selectedListing = myListings.find((l) => l.id === selectedId)
  const blocked = routeBlocked()

  return (
    <div className={`modal-overlay${overlayClassName ? ` ${overlayClassName}` : ''}`} onClick={onClose}>
      <div className="modal-content exchange-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Mainai — pasiūlyti savo telefoną</h3>
        <p className="exchange-modal__lead">
          Pasiūlykite savo skelbimą + priemoką šiam telefonui įsigyti.
        </p>

        <fieldset className="exchange-modal__routes">
          <legend className="exchange-modal__routes-legend">{t('exchange.routeLegend')}</legend>
          {PAYMENTS_ENABLED ? (
          <label className={`exchange-modal__route${exchangeRoute === 'warehouse' ? ' exchange-modal__route--active' : ''}`}>
            <input
              type="radio"
              name="exchange-route"
              value="warehouse"
              checked={exchangeRoute === 'warehouse'}
              onChange={() => setExchangeRoute('warehouse')}
            />
            <span>
              <strong>
                {t('exchange.routeWarehouse')}
                <span className="exchange-modal__beta">{t('exchange.routeWarehouseBeta')}</span>
              </strong>
              <small>{t('exchange.routeWarehouseHint')}</small>
              {passActive ? (
                <em className="exchange-modal__route-ok">{t('exchange.passActive')}</em>
              ) : (
                <em className="exchange-modal__route-warn">{t('exchange.passRequired')}</em>
              )}
            </span>
          </label>
          ) : null}
          <label className={`exchange-modal__route${exchangeRoute === 'direct' ? ' exchange-modal__route--active' : ''}`}>
            <input
              type="radio"
              name="exchange-route"
              value="direct"
              checked={exchangeRoute === 'direct'}
              onChange={() => setExchangeRoute('direct')}
            />
            <span>
              <strong>{t('exchange.routeDirect')}</strong>
              <small>{t('exchange.routeDirectHint')}</small>
              {verified ? (
                <em className="exchange-modal__route-ok">{t('exchange.verifyOk')}</em>
              ) : (
                <em className="exchange-modal__route-warn">{t('exchange.verifyRequired')}</em>
              )}
            </span>
          </label>
        </fieldset>

        {loadingMine && <p>Kraunama jūsų skelbimai…</p>}

        {!loadingMine && myListings.length === 0 && (
          <div className="exchange-modal__empty">
            <p>Neturite aktyvių skelbimų. Pirma įkelkite savo telefoną, kad galėtumėte juo mainytis.</p>
          </div>
        )}

        {!loadingMine && myListings.length > 0 && (
          <>
            <label className="exchange-modal__field">
              Jūsų telefonas mainams
              <select
                value={selectedId ?? ''}
                onChange={(e) => {
                  const id = parseInt(e.target.value, 10)
                  setSelectedId(id)
                  const l = myListings.find((m) => m.id === id)
                  if (l) {
                    const diff = Math.max(0, currentPrice - l.price)
                    setExtraCashText(formatSuggestedCash(diff))
                  }
                }}
              >
                {myListings.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.phone_model.manufacturer} {l.phone_model.model_name} — €{l.price.toFixed(2)}
                  </option>
                ))}
              </select>
            </label>

            <label className="exchange-modal__field">
              Priemoka pinigais (EUR)
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                value={extraCashText}
                onFocus={handleExtraCashFocus}
                onChange={(e) => handleExtraCashChange(e.target.value)}
              />
              <small className="exchange-modal__hint">
                Skirtumas tarp Jūsų skelbimo (€{selectedListing?.price.toFixed(2) ?? '0'}) ir šio telefono (€{currentPrice.toFixed(2)}) = <strong>€{Math.max(0, currentPrice - (selectedListing?.price ?? 0)).toFixed(2)}</strong>
              </small>
            </label>

            <div className="exchange-modal__summary">
              <strong>Bendra pasiūlymo vertė:</strong>
              <span>€{((selectedListing?.price ?? 0) + extraCash).toFixed(2)}</span>
            </div>
          </>
        )}

        <div className="modal-buttons">
          <button onClick={onClose} className="btn-secondary">Atšaukti</button>
          <button
            onClick={submit}
            disabled={loading || myListings.length === 0 || !selectedId || !!blocked}
            className="btn-primary"
          >
            {loading ? 'Siunčiama…' : 'Pasiūlyti mainus'}
          </button>
        </div>
      </div>
    </div>
  )
}
